import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList } from 'recharts';
import { getHubSpotSalesForecast, getHubSpotWinProbabilityDistribution, getHubSpotDealsDetailed, getHubSpotDealStageDuration, getHubSpotDealsByOwner, getHubSpotACVClosedByOwner, getHubSpotExpectedACVByOwner, getHubSpotWeightedTCVByStage, getHubSpotWeightedACVByStage, getHubSpotDealCountByStage } from '../services/api';
import { useLayout } from '../context/LayoutContext';
import useSortableData from '../hooks/useSortableData';

// Quarter date ranges
const QUARTER_LABELS = {
  1: 'Q1 (Jan 1 - Mar 31)',
  2: 'Q2 (Apr 1 - Jun 30)',
  3: 'Q3 (Jul 1 - Sep 30)',
  4: 'Q4 (Oct 1 - Dec 31)'
};

const QUARTER_DATE_RANGES = {
  1: { start: '1/1', end: '31/3' },
  2: { start: '1/4', end: '30/6' },
  3: { start: '1/7', end: '30/9' },
  4: { start: '1/10', end: '31/12' }
};

// Gauge Chart Component
function GaugeChart({ value, maxValue, title, subtitle, color, dateRange }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  // Data for the gauge (semi-circle)
  const data = [
    { value: percentage, color: color },
    { value: 100 - percentage, color: '#e2e8f0' }
  ];

  const formatValue = (val) => {
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(2)}M`;
    } else if (val >= 1000) {
      return `${(val / 1000).toFixed(0)}K`;
    }
    return val.toFixed(0);
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      padding: '20px',
      minHeight: '280px'
    }}>
      {/* Title */}
      <h3 style={{
        margin: '0 0 8px 0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#2563eb'
      }}>
        {title}
      </h3>

      {/* Date Range & Filters */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <span style={{
          background: '#dbeafe',
          color: '#1e40af',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '500'
        }}>
          {dateRange}
        </span>
        <span style={{
          background: '#f1f5f9',
          color: '#64748b',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px'
        }}>
          FILTERS (1)
        </span>
      </div>

      {/* Metric Label */}
      <div style={{
        fontSize: '12px',
        color: '#64748b',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: color
        }}></span>
        {subtitle}
      </div>

      {/* Gauge Chart */}
      <div style={{ position: 'relative', height: '160px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="70%"
              startAngle={180}
              endAngle={0}
              innerRadius={60}
              outerRadius={80}
              paddingAngle={0}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center Value */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -20%)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e293b'
          }}>
            {formatValue(value)}
          </div>
          <div style={{
            fontSize: '11px',
            color: '#64748b',
            marginTop: '2px'
          }}>
            {subtitle}
          </div>
        </div>

        {/* Min/Max Labels */}
        <div style={{
          position: 'absolute',
          bottom: '35px',
          left: '15%',
          fontSize: '11px',
          color: '#94a3b8'
        }}>
          0
        </div>
        <div style={{
          position: 'absolute',
          bottom: '35px',
          right: '10%',
          fontSize: '11px',
          color: '#94a3b8',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span style={{
            width: '20px',
            height: '2px',
            background: '#94a3b8'
          }}></span>
          {formatValue(maxValue)}
        </div>
      </div>
    </div>
  );
}

function QuarterlySalesDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [winProbDist, setWinProbDist] = useState(null);
  const [dealsDetailed, setDealsDetailed] = useState(null);
  const [stageDuration, setStageDuration] = useState(null);
  const [dealCountByStage, setDealCountByStage] = useState(null);
  const [dealsByOwner, setDealsByOwner] = useState(null);
  const [acvClosedByOwner, setAcvClosedByOwner] = useState(null);
  const [expectedAcvByOwner, setExpectedAcvByOwner] = useState(null);
  const [weightedTcvByStage, setWeightedTcvByStage] = useState(null);
  const [weightedAcvByStage, setWeightedAcvByStage] = useState(null);
  const [yearlyForecast, setYearlyForecast] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3)); // Current quarter
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOwnerFilter, setSelectedOwnerFilter] = useState('');
  const { mobileLayout, toggleLayout } = useLayout();
  const recordsPerPage = 10;
  const { sortedData: sortedDeals, requestSort: requestDealSort, getSortIndicator: getDealSortIndicator } = useSortableData(dealsDetailed?.deals || []);

  useEffect(() => {
    fetchData();
    setCurrentPage(1);
    setSelectedOwnerFilter('');
  }, [selectedYear, selectedQuarter]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    // Helper to fetch with retry on rate limit
    const fetchWithRetry = async (fetchFn, retries = 3, delay = 1000) => {
      for (let i = 0; i < retries; i++) {
        try {
          return await fetchFn();
        } catch (err) {
          if (err.response?.status === 429 && i < retries - 1) {
            await new Promise(r => setTimeout(r, delay * (i + 1)));
            continue;
          }
          throw err;
        }
      }
    };

    try {
      // Fetch core data first (sequentially to avoid rate limits)
      const forecastData = await fetchWithRetry(() => getHubSpotSalesForecast(selectedYear, selectedQuarter));
      setForecast(forecastData);

      // Fetch yearly totals (without quarter filter)
      const yearlyForecastData = await fetchWithRetry(() => getHubSpotSalesForecast(selectedYear, null));
      setYearlyForecast(yearlyForecastData);

      const winProbData = await fetchWithRetry(() => getHubSpotWinProbabilityDistribution(selectedYear, selectedQuarter, true, true));
      setWinProbDist(winProbData);

      const dealsData = await fetchWithRetry(() => getHubSpotDealsDetailed(selectedYear, selectedQuarter));
      setDealsDetailed(dealsData);

      // Fetch chart data with error handling (don't fail entire page if one fails)
      try {
        const stageDurationData = await fetchWithRetry(() => getHubSpotDealStageDuration(selectedYear, selectedQuarter));
        setStageDuration(stageDurationData);
      } catch (e) { console.error('Stage duration fetch failed:', e); }

      try {
        const dealCountByStageData = await fetchWithRetry(() => getHubSpotDealCountByStage(selectedYear, selectedQuarter));
        setDealCountByStage(dealCountByStageData);
      } catch (e) { console.error('Deal count by stage fetch failed:', e); }

      try {
        const dealsByOwnerData = await fetchWithRetry(() => getHubSpotDealsByOwner(selectedYear, selectedQuarter, true, true));
        setDealsByOwner(dealsByOwnerData);
      } catch (e) { console.error('Deals by owner fetch failed:', e); }

      try {
        const acvClosedData = await fetchWithRetry(() => getHubSpotACVClosedByOwner(selectedYear, selectedQuarter));
        setAcvClosedByOwner(acvClosedData);
      } catch (e) { console.error('ACV closed fetch failed:', e); }

      try {
        const expectedAcvData = await fetchWithRetry(() => getHubSpotExpectedACVByOwner(selectedYear, selectedQuarter));
        setExpectedAcvByOwner(expectedAcvData);
      } catch (e) { console.error('Expected ACV fetch failed:', e); }

      try {
        const weightedTcvData = await fetchWithRetry(() => getHubSpotWeightedTCVByStage(selectedYear, selectedQuarter));
        setWeightedTcvByStage(weightedTcvData);
      } catch (e) { console.error('Weighted TCV fetch failed:', e); }

      try {
        const weightedAcvData = await fetchWithRetry(() => getHubSpotWeightedACVByStage(selectedYear, selectedQuarter));
        setWeightedAcvByStage(weightedAcvData);
      } catch (e) { console.error('Weighted ACV fetch failed:', e); }

    } catch (err) {
      setError('Failed to fetch HubSpot data. Make sure the backend is running.');
      console.error('Error fetching HubSpot data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const metrics = forecast?.metrics || {};
  const quarterRange = QUARTER_DATE_RANGES[selectedQuarter];
  const dateRange = `Q${selectedQuarter} ${selectedYear}: FROM ${quarterRange.start}/${selectedYear} TO ${quarterRange.end}/${selectedYear}`;

  // Calculate max values for gauge scales (round up to nearest million)
  const maxTCV = Math.ceil((metrics.expected_tcv || 1) / 1000000) * 1000000;
  const maxACV = Math.ceil((metrics.expected_acv || 1) / 1000000) * 1000000;

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header" style={{
        display: 'flex',
        flexDirection: mobileLayout ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: mobileLayout ? 'flex-start' : 'center',
        marginBottom: '20px',
        gap: mobileLayout ? '12px' : '0'
      }}>
        <div>
          <h1 style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: mobileLayout ? '16px' : undefined
          }}>
            <span style={{ color: '#f97316' }}>&#9734;</span>
            {mobileLayout ? 'Quarterly Sales Dashboard' : 'Quarterly Sales Revenue Forecast Dashboard'}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Layout Toggle Button */}
          <button
            onClick={toggleLayout}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: mobileLayout ? '#3b82f6' : '#fff',
              color: mobileLayout ? '#fff' : '#1e293b',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            {mobileLayout ? 'Mobile' : 'Desktop'}
          </button>
          {/* Quarter Selector */}
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '2px solid #f97316',
              fontSize: '14px',
              cursor: 'pointer',
              background: '#fff7ed',
              color: '#c2410c',
              fontWeight: '600'
            }}
          >
            {[1, 2, 3, 4].map(q => (
              <option key={q} value={q}>{QUARTER_LABELS[q]}</option>
            ))}
          </select>
          {/* Year Selector */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Summary Stats */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          background: '#faf5ff',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #d8b4fe'
        }}>
          <div style={{ fontSize: '12px', color: '#7c3aed' }}>Total Yearly Deals</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#7c3aed' }}>
            {yearlyForecast?.deal_count || 0}
          </div>
        </div>
        <div style={{
          background: '#f0fdf4',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #86efac'
        }}>
          <div style={{ fontSize: '12px', color: '#166534' }}>Total Quarterly Deals</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>
            {forecast?.deal_count || 0}
          </div>
        </div>
        <div style={{
          background: '#fff7ed',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '2px solid #fb923c'
        }}>
          <div style={{ fontSize: '12px', color: '#c2410c' }}>Quarter</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#ea580c' }}>
            Q{selectedQuarter}
          </div>
        </div>
        <div style={{
          background: '#eff6ff',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #93c5fd'
        }}>
          <div style={{ fontSize: '12px', color: '#1e40af' }}>Year</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d4ed8' }}>
            {selectedYear}
          </div>
        </div>
      </div>

      {/* Gauge Charts Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobileLayout ? '1fr' : 'repeat(2, 1fr)',
        gap: mobileLayout ? '12px' : '20px',
        marginBottom: '24px'
      }}>
        {/* Expected TCV */}
        <GaugeChart
          value={metrics.expected_tcv || 0}
          maxValue={maxTCV}
          title={`Expected TCV total funnel_Q${selectedQuarter}${selectedYear}`}
          subtitle="(Sum) Amount"
          color="#3b82f6"
          dateRange={dateRange}
        />

        {/* Expected ACV */}
        <GaugeChart
          value={metrics.expected_acv || 0}
          maxValue={maxACV}
          title={`Expected ACV total funnel_Q${selectedQuarter}${selectedYear}`}
          subtitle="(Sum) Expected ACV"
          color="#06b6d4"
          dateRange={dateRange}
        />

        {/* Weighted TCV */}
        <GaugeChart
          value={metrics.weighted_tcv || 0}
          maxValue={maxTCV}
          title={`Close Likelihood Weighted TCV_Q${selectedQuarter}_${selectedYear}`}
          subtitle="(Sum) Deal stage weighted amount"
          color="#8b5cf6"
          dateRange={dateRange}
        />

        {/* Weighted ACV */}
        <GaugeChart
          value={metrics.weighted_acv || 0}
          maxValue={maxACV}
          title={`Close Likelihood Weighted ACV_Q${selectedQuarter}_${selectedYear}`}
          subtitle="(Sum) Weighted ACV"
          color="#10b981"
          dateRange={dateRange}
        />
      </div>

      {/* Weighted TCV and ACV by Deal Stage - Bar Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobileLayout ? '1fr' : 'repeat(2, 1fr)',
        gap: mobileLayout ? '12px' : '20px',
        marginBottom: '24px'
      }}>
        {/* Weighted TCV based on deal stage */}
        {weightedTcvByStage?.data && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
              Close Likelihood Weighted TCV_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
              <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>FILTERS (1)</span>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', fontSize: '10px', flexWrap: 'wrap' }}>
              {weightedTcvByStage.data.map((item, idx) => {
                const colors = ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'];
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[idx % colors.length] }}></span>
                    {item.stage.replace(' (Sales Pipeline)', '')}
                  </div>
                );
              })}
            </div>

            <div style={{ height: '280px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weightedTcvByStage.data} margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                  <XAxis
                    dataKey="stage"
                    tick={{ fontSize: 11, angle: -20, textAnchor: 'end' }}
                    interval={0}
                    height={60}
                    label={{ value: 'Deal Stage', position: 'bottom', offset: 40, fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                    label={{ value: '(Sum) Deal stage weighted amount', angle: -90, position: 'insideLeft', offset: -45, fontSize: 10 }}
                  />
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={25}>
                    {weightedTcvByStage.data.map((_, idx) => {
                      const colors = ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'];
                      return <Cell key={idx} fill={colors[idx % colors.length]} />;
                    })}
                    <LabelList dataKey="value" position="top" fontSize={9} formatter={(v) => v.toLocaleString()} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Weighted ACV by deal stage */}
        {weightedAcvByStage?.data && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
              Close Likelihood Weighted ACV_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
              <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>FILTERS (1)</span>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', fontSize: '10px', flexWrap: 'wrap' }}>
              {weightedAcvByStage.data.map((item, idx) => {
                const colors = ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'];
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[idx % colors.length] }}></span>
                    {item.stage.replace(' (Sales Pipeline)', '')}
                  </div>
                );
              })}
            </div>

            <div style={{ height: '280px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weightedAcvByStage.data} margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                  <XAxis
                    dataKey="stage"
                    tick={{ fontSize: 11, angle: -20, textAnchor: 'end' }}
                    interval={0}
                    height={60}
                    label={{ value: 'Deal Stage', position: 'bottom', offset: 40, fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                    label={{ value: '(Sum) Weighted ACV', angle: -90, position: 'insideLeft', offset: -45, fontSize: 10 }}
                  />
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={25}>
                    {weightedAcvByStage.data.map((_, idx) => {
                      const colors = ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'];
                      return <Cell key={idx} fill={colors[idx % colors.length]} />;
                    })}
                    <LabelList dataKey="value" position="top" fontSize={9} formatter={(v) => v.toLocaleString()} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Win Probability Distribution Pie Charts */}
      {winProbDist && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: mobileLayout ? '1fr' : 'repeat(3, 1fr)',
          gap: mobileLayout ? '12px' : '20px',
          marginBottom: '24px'
        }}>
          {/* TCV Distribution */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '16px',
            overflow: 'visible'
          }}>
            <h3 style={{
              margin: '0 0 6px 0',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2563eb'
            }}>
              Expected TCV distribution by Win Prob_Q{selectedQuarter}{selectedYear}
            </h3>
            <div style={{
              background: '#dbeafe',
              color: '#1e40af',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '500',
              display: 'inline-block',
              marginBottom: '8px'
            }}>
              {dateRange}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '8px', fontSize: '11px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316' }}></span>
                Above the line
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#06b6d4' }}></span>
                Below the line
              </div>
            </div>

            {/* Pie Chart */}
            <div style={{ height: '220px', overflow: 'visible', paddingTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Above', value: winProbDist.tcv.above_the_line.value, color: '#f97316' },
                      { name: 'Below', value: winProbDist.tcv.below_the_line.value, color: '#06b6d4' }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    label={({ percent, value }) =>
                      `${(percent * 100).toFixed(0)}% (${value.toLocaleString()})`
                    }
                    labelLine={true}
                  >
                    <Cell fill="#f97316" />
                    <Cell fill="#06b6d4" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ACV Distribution */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '16px',
            overflow: 'visible'
          }}>
            <h3 style={{
              margin: '0 0 6px 0',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2563eb'
            }}>
              Expected ACV distribution by Win Prob_Q{selectedQuarter}{selectedYear}
            </h3>
            <div style={{
              background: '#dbeafe',
              color: '#1e40af',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '500',
              display: 'inline-block',
              marginBottom: '8px'
            }}>
              {dateRange}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '8px', fontSize: '11px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316' }}></span>
                Above the line
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#06b6d4' }}></span>
                Below the line
              </div>
            </div>

            {/* Pie Chart */}
            <div style={{ height: '220px', overflow: 'visible', paddingTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Above', value: winProbDist.acv.above_the_line.value, color: '#f97316' },
                      { name: 'Below', value: winProbDist.acv.below_the_line.value, color: '#06b6d4' }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    label={({ percent, value }) =>
                      `${(percent * 100).toFixed(0)}% (${value.toLocaleString()})`
                    }
                    labelLine={true}
                  >
                    <Cell fill="#f97316" />
                    <Cell fill="#06b6d4" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Count of Deals Distribution */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '16px',
            overflow: 'visible'
          }}>
            <h3 style={{
              margin: '0 0 6px 0',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2563eb'
            }}>
              Count of Deals by Win Prob_Q{selectedQuarter}{selectedYear}
            </h3>
            <div style={{
              background: '#dbeafe',
              color: '#1e40af',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '500',
              display: 'inline-block',
              marginBottom: '8px'
            }}>
              {dateRange}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '8px', fontSize: '11px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316' }}></span>
                Above the line
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#06b6d4' }}></span>
                Below the line
              </div>
            </div>

            {/* Pie Chart */}
            <div style={{ height: '220px', overflow: 'visible', paddingTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Above', value: winProbDist.count?.above_the_line?.value || 0 },
                      { name: 'Below', value: winProbDist.count?.below_the_line?.value || 0 }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    label={({ percent, value }) =>
                      `${(percent * 100).toFixed(0)}% (${value})`
                    }
                    labelLine={true}
                  >
                    <Cell fill="#f97316" />
                    <Cell fill="#06b6d4" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}


      {/* Detailed Deals Table */}
      {dealsDetailed?.deals && dealsDetailed.deals.length > 0 && (() => {
        // Get unique deal owners for filter dropdown
        const uniqueOwners = [...new Set(dealsDetailed.deals.map(d => d.deal_owner).filter(Boolean))].sort();

        // Filter deals by selected owner (use sorted data)
        const filteredDeals = selectedOwnerFilter
          ? sortedDeals.filter(d => d.deal_owner === selectedOwnerFilter)
          : sortedDeals;

        const totalPages = Math.ceil(filteredDeals.length / recordsPerPage);
        const startIndex = (currentPage - 1) * recordsPerPage;
        const endIndex = startIndex + recordsPerPage;
        const paginatedDeals = filteredDeals.slice(startIndex, endIndex);

        return (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          padding: '20px',
          marginTop: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ margin: '0', fontSize: '16px', color: '#2563eb' }}>
              Sales Revenue Forecast & Commit_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                {dateRange}
              </span>
              <select
                value={selectedOwnerFilter}
                onChange={(e) => {
                  setSelectedOwnerFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  fontSize: '11px',
                  cursor: 'pointer',
                  background: selectedOwnerFilter ? '#fef3c7' : '#fff',
                  color: '#1e293b'
                }}
              >
                <option value="">All Deal Owners</option>
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
              <span style={{
                background: '#f0fdf4',
                color: '#166534',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                {filteredDeals.length} deals {selectedOwnerFilter && `(filtered)`}
              </span>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1600px', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                  {[
                    { key: 'company', label: 'Company' },
                    { key: 'sales_region', label: 'Sales Region' },
                    { key: 'deal_owner', label: 'Deal Owner' },
                    { key: 'deal_name', label: 'Deal Name' },
                    { key: 'deal_source', label: 'Deal Source' },
                    { key: 'currency', label: 'Currency' },
                    { key: 'amount', label: 'Amount', align: 'right' },
                    { key: 'actual_acv', label: 'Actual ACV', align: 'right' },
                    { key: 'expected_acv', label: 'Expected ACV', align: 'right' },
                    { key: 'actual_duration', label: 'Actual Duration' },
                    { key: 'deal_stage', label: 'Deal Stage' },
                    { key: 'close_plan', label: 'Close Plan %' },
                    { key: 'close_date', label: 'Close Date' },
                    { key: 'compelling_reason', label: 'Compelling Reason %' },
                    { key: 'win_probability_pct', label: 'Win Probability', align: 'center' },
                    { key: 'above_the_line', label: 'Above The Line' }
                  ].map(col => (
                    <th key={col.key} onClick={() => { requestDealSort(col.key); setCurrentPage(1); }} style={{ textAlign: col.align || 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                      {col.label}{getDealSortIndicator(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedDeals.map((deal, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.company}>{deal.company || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.sales_region || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.deal_owner || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_name}>{deal.deal_name || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.deal_source || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.currency || 'USD'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {deal.amount > 0 ? deal.amount.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {deal.actual_acv > 0 ? deal.actual_acv.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {deal.expected_acv > 0 ? deal.expected_acv.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.actual_duration || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_stage}>{deal.deal_stage || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.close_plan}>{deal.close_plan || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.close_date || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.compelling_reason}>{deal.compelling_reason || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center', whiteSpace: 'nowrap' }}>{deal.win_probability_pct || 0}%</td>
                    <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.above_the_line || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            flexDirection: mobileLayout ? 'column' : 'row',
            justifyContent: mobileLayout ? 'center' : 'space-between',
            alignItems: 'center',
            marginTop: '16px',
            padding: '12px 0',
            gap: '10px'
          }}>
            <div style={{ fontSize: mobileLayout ? '11px' : '13px', color: '#64748b', textAlign: 'center' }}>
              {mobileLayout ? `${startIndex + 1}-${Math.min(endIndex, filteredDeals.length)} of ${filteredDeals.length}` : `Showing ${filteredDeals.length > 0 ? startIndex + 1 : 0} to ${Math.min(endIndex, filteredDeals.length)} of ${filteredDeals.length} deals`}
              {selectedOwnerFilter && !mobileLayout && ` (filtered by ${selectedOwnerFilter})`}
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: mobileLayout ? '6px 10px' : '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  background: currentPage === 1 ? '#f1f5f9' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: mobileLayout ? '11px' : '12px',
                  color: currentPage === 1 ? '#94a3b8' : '#1e40af'
                }}
              >
                {mobileLayout ? '<<' : 'First'}
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: mobileLayout ? '6px 10px' : '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  background: currentPage === 1 ? '#f1f5f9' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: mobileLayout ? '11px' : '12px',
                  color: currentPage === 1 ? '#94a3b8' : '#1e40af'
                }}
              >
                {mobileLayout ? '<' : 'Previous'}
              </button>
              <span style={{ padding: '4px 8px', fontSize: mobileLayout ? '11px' : '13px', color: '#1e293b', whiteSpace: 'nowrap' }}>
                {currentPage}/{totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: mobileLayout ? '6px 10px' : '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: mobileLayout ? '11px' : '12px',
                  color: currentPage === totalPages ? '#94a3b8' : '#1e40af'
                }}
              >
                {mobileLayout ? '>' : 'Next'}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: mobileLayout ? '6px 10px' : '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: mobileLayout ? '11px' : '12px',
                  color: currentPage === totalPages ? '#94a3b8' : '#1e40af'
                }}
              >
                {mobileLayout ? '>>' : 'Last'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* New Charts Section - 2x2 Grid (below deals table) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobileLayout ? '1fr' : 'repeat(2, 1fr)',
        gap: mobileLayout ? '12px' : '20px',
        marginTop: '24px'
      }}>
        {/* Deal Count by Stage - Pie Chart */}
        {dealCountByStage?.data && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
              Deal count by deal stage_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
              <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>FILTERS (1)</span>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', fontSize: '11px', flexWrap: 'wrap' }}>
              {dealCountByStage.data.map((item, idx) => {
                const colors = ['#f97316', '#06b6d4', '#8b5cf6', '#eab308', '#10b981', '#ec4899'];
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors[idx % colors.length] }}></span>
                    {item.stage}
                  </div>
                );
              })}
            </div>

            {/* Pie Chart */}
            <div style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dealCountByStage.data.map((item, idx) => {
                      const colors = ['#f97316', '#06b6d4', '#8b5cf6', '#eab308', '#10b981', '#ec4899'];
                      return { ...item, color: colors[idx % colors.length] };
                    })}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    label={({ percentage, count }) => `${percentage}% (${count})`}
                    labelLine={true}
                  >
                    {dealCountByStage.data.map((_, idx) => {
                      const colors = ['#f97316', '#06b6d4', '#8b5cf6', '#eab308', '#10b981', '#ec4899'];
                      return <Cell key={idx} fill={colors[idx % colors.length]} />;
                    })}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Quarterly Number of Deals by Deal Owner - Bar Chart */}
        {dealsByOwner?.data && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
              Number of deals by deal owner_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '500' }}>LIMITED DATA</span>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
            </div>

            <div style={{ height: mobileLayout ? '250px' : '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dealsByOwner.data.slice(0, 5)} layout="vertical" margin={mobileLayout ? { left: 80, right: 30 } : { left: 120, right: 40 }}>
                  <XAxis type="number" domain={[0, 50]} ticks={mobileLayout ? [0, 25, 50] : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50]} tick={{ fontSize: mobileLayout ? 9 : 10 }} />
                  <YAxis type="category" dataKey="owner" tick={{ fontSize: mobileLayout ? 9 : 11 }} width={mobileLayout ? 70 : 120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#E8927C" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: mobileLayout ? 9 : 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Quarterly ACV Closed Amount by Deal Owner */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          padding: '20px'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
            ACV closed amount by deal owner_Q{selectedQuarter}_{selectedYear}
          </h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '500' }}>LIMITED DATA</span>
            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
          </div>

          {/* Display as cards - use dealsByOwner for owner names if acvClosedByOwner is empty */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', marginTop: '20px' }}>
            {(dealsByOwner?.data?.slice(0, 5) || []).map((item, idx) => {
              const closedAcv = acvClosedByOwner?.data?.find(d => d.owner === item.owner)?.acv || 0;
              return (
                <div key={idx} style={{ textAlign: 'center', minWidth: '120px' }}>
                  <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '500' }}>
                    {item.owner}
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '300', color: '#06b6d4' }}>
                    {closedAcv > 0 ? closedAcv.toLocaleString() : '0'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quarterly Expected ACV by Deal Owner - Bar Chart */}
        {expectedAcvByOwner?.data && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>
              Expected ACV by deal owner_Q{selectedQuarter}_{selectedYear}
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '500' }}>LIMITED DATA</span>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{dateRange}</span>
              <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>FILTERS (1)</span>
            </div>

            <div style={{ height: mobileLayout ? '280px' : '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expectedAcvByOwner.data.slice(0, 5)} layout="vertical" margin={mobileLayout ? { left: 10, right: 10, top: 10, bottom: 10 } : { left: 120, right: 80 }}>
                  <XAxis type="number" tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} domain={[0, 'auto']} tick={{ fontSize: mobileLayout ? 9 : 10 }} />
                  <YAxis type="category" dataKey="owner" tick={{ fontSize: mobileLayout ? 8 : 11 }} width={mobileLayout ? 100 : 120} />
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Bar dataKey="acv" fill="#E8927C" radius={[0, 4, 4, 0]} label={mobileLayout ? false : { position: 'right', fontSize: 10, formatter: (v) => v.toLocaleString() }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuarterlySalesDashboard;
